/**
 * Ranking helper for the "Spotlight" recommendation shown on the My Universe tab.
 *
 * A universe is a set of genre-labelled clusters, each with its own
 * pre-sorted `recommendations` array. The spotlight picks the single highest
 * value recommendation across all clusters given the user's current feedback
 * state, so that when an artist gets rated (favorite / neutral / dislike /
 * discovered) the next best artist naturally takes its place.
 *
 * Scoring blends:
 *   - composite / match score    (base strength of the recommendation)
 *   - overlap with user seeds    (more seed connections = more relevant)
 *   - cluster size               (pick from clusters that dominate the user's taste)
 *   - tier bias                  (prefer approachable "popular" picks for a headline)
 *   - chain-bridge penalty       (multi-hop bridges are niche, not headline-worthy)
 */

function scoreRecommendation(rec, clusterWeight, totalSeeds) {
  const base = typeof rec.compositeScore === 'number'
    ? rec.compositeScore
    : typeof rec.matchScore === 'number'
      ? rec.matchScore
      : 0;

  const overlapCount = typeof rec.overlapCount === 'number'
    ? rec.overlapCount
    : Array.isArray(rec.relatedSeedNames)
      ? rec.relatedSeedNames.length
      : Array.isArray(rec.suggestedBy)
        ? rec.suggestedBy.length
        : 1;

  const overlapBoost = 0.15 * Math.min(overlapCount / Math.max(totalSeeds, 1), 1);
  const clusterBoost = 0.10 * clusterWeight;
  const popularBoost = rec.tier === 'popular' ? 0.05 : 0;
  const chainPenalty = rec.discoveryMethod === 'chain_bridge' ? -0.20 : 0;

  return base + overlapBoost + clusterBoost + popularBoost + chainPenalty;
}

/**
 * Rank every recommendation in the universe, excluding artists the user
 * has already rated. Returns a sorted array of candidates the spotlight
 * can cycle through; the caller decides which one to show.
 *
 * @param {object} universeData - universe graph with `clusters`
 * @param {Set<string>} excludedNames - lowercase trimmed names to skip
 * @returns {Array<{recommendation, clusterColor, clusterLabel, score}>}
 */
export function rankSpotlightRecommendations(universeData, excludedNames) {
  const clusters = universeData?.clusters;
  if (!Array.isArray(clusters) || clusters.length === 0) return [];

  const totalSeeds = clusters.reduce(
    (sum, c) => sum + (Array.isArray(c.members) ? c.members.length : 0),
    0
  ) || 1;

  const scored = [];

  for (const cluster of clusters) {
    const recs = cluster?.recommendations;
    if (!Array.isArray(recs) || recs.length === 0) continue;

    const memberCount = Array.isArray(cluster.members) ? cluster.members.length : 0;
    const clusterWeight = memberCount / totalSeeds;

    for (const rec of recs) {
      if (!rec?.name) continue;
      const key = rec.name.toLowerCase().trim();
      if (excludedNames && excludedNames.has(key)) continue;

      const score = scoreRecommendation(rec, clusterWeight, totalSeeds);
      scored.push({
        recommendation: rec,
        clusterColor: cluster.color || null,
        clusterLabel: cluster.label || null,
        score,
      });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored;
}

/**
 * Look up a specific recommendation by name across all clusters.
 * Used to keep showing a rated artist until the user manually moves on.
 *
 * @param {object} universeData
 * @param {string} name - case-insensitive artist name
 * @returns {{recommendation, clusterColor, clusterLabel} | null}
 */
export function findSpotlightByName(universeData, name) {
  if (!name) return null;
  const clusters = universeData?.clusters;
  if (!Array.isArray(clusters)) return null;
  const target = name.toLowerCase().trim();
  for (const cluster of clusters) {
    const recs = cluster?.recommendations;
    if (!Array.isArray(recs)) continue;
    for (const rec of recs) {
      if (rec?.name?.toLowerCase().trim() === target) {
        return {
          recommendation: rec,
          clusterColor: cluster.color || null,
          clusterLabel: cluster.label || null,
        };
      }
    }
  }
  return null;
}
